<script setup lang="ts">
// 问答卡片（ask_user 动作）：把「需要用户拍板的事」渲染成单选 / 多选 / 自行输入。
//
// 形态对齐 Claude Code 的 AskUserQuestion，语义对齐 MCP elicitation 的 form 模式：
// - 每题 2~4 个选项（服务端已校验），选项文案与说明都由服务端下发；
// - 「其他（自行输入）」与「跳过」是**界面常驻能力**，不由模型生成——预置选项永远覆盖不全；
// - 用户也可以完全不理会这张卡片、直接在输入框里说别的（输入区从不阻塞）；
// - 单选点一下就是一次完整回答；多题或多选时需要显式确认（否则先答的那题会被后一题挤掉）；
// - 提交后卡片就地禁用：回答已经作为一条用户消息发出，重复点击只会重复提问。
//
// 无障碍：单选组按 WAI-ARIA radiogroup 语义（容器 role、子项 role=radio + aria-checked），
// 选项用原生 button（键盘可达、Space/Enter 可激活），Tab 可整体进出。
import { computed, ref } from "vue";
import type { AiAction, AiAskPayload, AiAskQuestion } from "@/apis/ai.ts";
import { useAiCoachStore } from "@/stores/aiCoach.ts";

const props = defineProps<{ action: AiAction }>();
const emit = defineEmits<{ (e: "handled", action: AiAction): void }>();

const store = useAiCoachStore();

/** 载荷解析：服务端已校验结构，这里只做防御性判空（老版本载荷可能形状不符） */
const payload = computed<AiAskPayload | null>(() => {
  const raw = props.action.payload as unknown as AiAskPayload | undefined;
  if (!raw || !Array.isArray(raw.questions) || raw.questions.length === 0) {
    return null;
  }
  return raw;
});

const questions = computed<AiAskQuestion[]>(() => payload.value?.questions ?? []);
const answered = computed(() => props.action.disabled === true);
const answeredHint = computed(() => {
  const reason = props.action.reason;
  return reason === undefined || reason === null || reason === "" ? "已回答" : String(reason);
});

const busy = ref(false);
/** 各题选中的选项下标（单选至多一个，多选可多个） */
const picked = ref<Record<number, number[]>>({});
/** 各题是否展开了「其他」输入框 / 输入内容 */
const otherOpen = ref<Record<number, boolean>>({});
const otherText = ref<Record<number, string>>({});

/** 只有一道单选题时，点选即提交；多题或多选需要确认（避免先答的那题被挤掉） */
const needConfirm = computed(
  () => questions.value.length > 1 || questions.value.some((question) => question.multi_select === true),
);
const hasPick = computed(() => Object.values(picked.value).some((list) => list.length > 0));

function isPicked(questionIndex: number, optionIndex: number): boolean {
  return (picked.value[questionIndex] ?? []).includes(optionIndex);
}

function toggleMulti(questionIndex: number, optionIndex: number) {
  const current = picked.value[questionIndex] ?? [];
  picked.value[questionIndex] = current.includes(optionIndex)
    ? current.filter((item) => item !== optionIndex)
    : [...current, optionIndex];
}

/** 某一题的答案文本：选中的选项（按服务端给的 reply）+ 自行输入的内容 */
function repliesOf(questionIndex: number, question: AiAskQuestion): string[] {
  const replies = (picked.value[questionIndex] ?? [])
    .map((optionIndex) => question.options[optionIndex]?.reply ?? "")
    .filter((item) => item !== "");
  const custom = (otherText.value[questionIndex] ?? "").trim();
  if (custom !== "") {
    // 用户自填的内容就是答案本身（不把「其他」当作答案值）
    replies.push(custom);
  }
  return replies;
}

/**
 * 拼成一条用户消息。
 *
 * 多题时逐行标注「哪一题 → 答案」，否则模型无法把后一轮的答案对上问题；
 * 单题直接发答案原文（最自然，也最少噪音）。
 */
function buildMessage(): string {
  const lines: string[] = [];
  questions.value.forEach((question, questionIndex) => {
    const replies = repliesOf(questionIndex, question);
    if (replies.length === 0) {
      return;
    }
    const text = replies.join("、");
    if (questions.value.length <= 1) {
      lines.push(text);
      return;
    }
    const label = question.header?.trim() || question.question;
    lines.push(`${label}：${text}`);
  });
  return lines.join("\n");
}

async function submitMessage(message: string) {
  const text = message.trim();
  if (text === "" || busy.value || answered.value) {
    return;
  }
  busy.value = true;
  try {
    // 先就地禁用再发送：回答一旦发出，这张卡片的历史使命就结束了
    store.markActionAnswered(props.action);
    emit("handled", props.action);
    await store.send(text);
  } finally {
    busy.value = false;
  }
}

function pickSingle(questionIndex: number, optionIndex: number, question: AiAskQuestion) {
  picked.value[questionIndex] = [optionIndex];
  if (!needConfirm.value) {
    void submitMessage(repliesOf(questionIndex, question).join("、"));
  }
}

function confirmPicked() {
  void submitMessage(buildMessage());
}

function openOther(questionIndex: number) {
  otherOpen.value[questionIndex] = true;
}

function submitOther(questionIndex: number, question: AiAskQuestion) {
  const custom = (otherText.value[questionIndex] ?? "").trim();
  if (custom === "") {
    return;
  }
  // 自填内容单独成答：不再要求用户回头点一次确认
  void submitMessage(questions.value.length <= 1 ? custom : `${question.header?.trim() || question.question}：${custom}`);
}

/** 跳过：按服务端给的措辞请模型自己按常见情况处理并说明假设 */
function skip() {
  void submitMessage(payload.value?.skip_reply || "跳过这个问题：你按常见情况处理，并说明你假设了什么。");
}
</script>

<template>
  <div v-if="payload" class="ai-ask">
    <div v-for="(question, questionIndex) in questions" :key="questionIndex" class="ai-ask-question">
      <div class="ai-ask-head">
        <span v-if="question.header" class="ai-ask-header">{{ question.header }}</span>
        <span class="ai-ask-title">{{ question.question }}</span>
        <span v-if="question.multi_select && payload.multi_hint" class="ai-ask-tip">{{ payload.multi_hint }}</span>
      </div>

      <div
        class="ai-ask-options"
        :role="question.multi_select ? 'group' : 'radiogroup'"
        :aria-label="question.question"
      >
        <button
          v-for="(option, optionIndex) in question.options"
          :key="optionIndex"
          type="button"
          class="ai-ask-option"
          :class="{ 'is-picked': isPicked(questionIndex, optionIndex) }"
          :role="question.multi_select ? 'checkbox' : 'radio'"
          :aria-checked="isPicked(questionIndex, optionIndex)"
          :disabled="answered || busy"
          @click="question.multi_select ? toggleMulti(questionIndex, optionIndex) : pickSingle(questionIndex, optionIndex, question)"
        >
          <span class="ai-ask-option-label">{{ option.label }}</span>
          <span v-if="option.description" class="ai-ask-option-desc">{{ option.description }}</span>
        </button>
      </div>

      <!-- 其他（自行输入）：常驻逃生口，模型不能也不需要自己造这个选项 -->
      <div class="ai-ask-other">
        <button
          v-if="!otherOpen[questionIndex]"
          type="button"
          class="ai-ask-other-toggle"
          :disabled="answered || busy"
          @click="openOther(questionIndex)"
        >
          {{ payload.other_label || "其他（自行输入）" }}
        </button>
        <div v-else class="ai-ask-other-input">
          <t-input
            v-model="otherText[questionIndex]"
            size="small"
            :placeholder="payload.other_hint || '请输入你的答案'"
            :disabled="answered || busy"
            @enter="submitOther(questionIndex, question)"
          />
          <t-button
            size="small"
            theme="primary"
            :disabled="answered || busy || !(otherText[questionIndex] ?? '').trim()"
            @click="submitOther(questionIndex, question)"
          >
            {{ payload.other_submit_label || "发送" }}
          </t-button>
        </div>
      </div>
    </div>

    <div class="ai-ask-foot">
      <t-button
        v-if="needConfirm"
        size="small"
        theme="primary"
        :disabled="answered || busy || !hasPick"
        @click="confirmPicked"
      >
        {{ payload.multi_confirm_label || "确认选择" }}
      </t-button>
      <t-button size="small" variant="text" :disabled="answered || busy" @click="skip">
        {{ payload.skip_label || "跳过" }}
      </t-button>
      <span v-if="answered" class="ai-ask-answered">{{ answeredHint }}</span>
    </div>
  </div>

  <!-- 载荷缺失（如来自更早版本的会话）：不静默丢弃，也不假装可点 -->
  <div v-else class="ai-ask ai-ask-broken">
    <span class="ai-ask-answered">这张问答卡片缺少内容，请重新提问</span>
  </div>
</template>

<style scoped>
/* 卡片：与动作卡片同一区域的视觉，但内容是一组问题 */
.ai-ask {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid var(--td-component-border);
  border-left: 3px solid var(--td-brand-color);
  border-radius: var(--td-radius-default);
  background-color: var(--td-bg-color-secondarycontainer);
}

.ai-ask-broken {
  border-left-color: var(--td-warning-color);
}

.ai-ask-question {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ai-ask-head {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.ai-ask-header {
  padding: 0 6px;
  border-radius: var(--td-radius-default);
  background-color: var(--td-brand-color-light);
  color: var(--td-brand-color);
  font-size: 11px;
  line-height: 1.7;
}

.ai-ask-title {
  font-size: 13px;
  color: var(--td-text-color-primary);
}

.ai-ask-tip {
  font-size: 11px;
  color: var(--td-text-color-placeholder);
}

/* 选项：整块可点（按钮而非散文），一行 label + 一行说明 */
.ai-ask-options {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ai-ask-option {
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 100%;
  padding: 5px 10px;
  border: 1px solid var(--td-component-border);
  border-radius: var(--td-radius-default);
  background-color: var(--td-bg-color-container);
  text-align: left;
  cursor: pointer;
}

.ai-ask-option:hover:not(:disabled) {
  border-color: var(--td-brand-color);
}

.ai-ask-option.is-picked {
  border-color: var(--td-brand-color);
  background-color: var(--td-brand-color-light);
}

.ai-ask-option:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.ai-ask-option-label {
  font-size: 12px;
  color: var(--td-text-color-primary);
}

.ai-ask-option-desc {
  font-size: 11px;
  line-height: 1.5;
  color: var(--td-text-color-placeholder);
}

/* 其他（自行输入）：弱化的次要入口 */
.ai-ask-other-toggle {
  padding: 2px 0;
  border: none;
  background: transparent;
  color: var(--td-text-color-secondary);
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}

.ai-ask-other-toggle:disabled {
  color: var(--td-text-color-disabled);
  cursor: not-allowed;
}

.ai-ask-other-input {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ai-ask-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ai-ask-answered {
  font-size: 11px;
  color: var(--td-text-color-placeholder);
}
</style>
